import { useState, useEffect } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  ListOrdered,
  Users,
  Loader2
} from 'lucide-react';

import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Card, CardContent } from '../../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { getAuthHeaders, adminFetch } from '../../../services/adminApi';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function NewsletterListsTab({ showMessage }) {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [viewingRecipients, setViewingRecipients] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  useEffect(() => {
    fetchLists();
  }, []);

  const fetchLists = async () => {
    setLoading(true);
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/lists`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setLists(data);
      } else {
        showMessage('Failed to load lists', 'error');
      }
    } catch (error) {
      console.error('Error fetching lists:', error);
      showMessage('Failed to load lists', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (list) => {
    setEditing(list);
    setFormData({ name: list.name, description: list.description || '' });
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditing(null);
    setFormData({ name: '', description: '' });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showMessage('List name is required', 'error');
      return;
    }

    setSaving(true);
    try {
      const url = editing
        ? `${API_BASE}/api/admin/newsletter/lists/${editing.id}`
        : `${API_BASE}/api/admin/newsletter/lists`;
      const method = editing ? 'PUT' : 'POST';

      const response = await adminFetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        showMessage(editing ? 'List updated' : 'List created');
        setShowForm(false);
        setEditing(null);
        fetchLists();
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to save list', 'error');
      }
    } catch (error) {
      console.error('Error saving list:', error);
      showMessage('Failed to save list', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (list) => {
    if (!window.confirm(`Are you sure you want to delete "${list.name}"? This will remove all subscribers from this list.`)) {
      return;
    }

    try {
      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/lists/${list.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        showMessage('List deleted');
        fetchLists();
      } else {
        showMessage('Failed to delete list', 'error');
      }
    } catch (error) {
      console.error('Error deleting list:', error);
      showMessage('Failed to delete list', 'error');
    }
  };

  const handleViewRecipients = async (list) => {
    setViewingRecipients(list);
    setLoadingRecipients(true);
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/lists/${list.id}/recipients`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setRecipients(data);
      }
    } catch (error) {
      console.error('Error fetching recipients:', error);
    } finally {
      setLoadingRecipients(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Add Button */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {lists.length} list{lists.length !== 1 ? 's' : ''}
        </p>
        <Button onClick={handleCreate} variant="adminPrimary">
          <Plus className="h-4 w-4 mr-2" />
          New List
        </Button>
      </div>

      {/* Lists Grid */}
      {lists.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <ListOrdered className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>No email lists yet</p>
          <p className="text-sm">Create your first list to start collecting subscribers</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {lists.map((list) => (
            <Card key={list.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">{list.name}</h3>
                    {list.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{list.description}</p>
                    )}
                    <button
                      onClick={() => handleViewRecipients(list)}
                      className="flex items-center gap-1 mt-2 text-sm text-red-600 hover:text-red-700"
                    >
                      <Users className="h-4 w-4" />
                      {list.recipient_count} subscriber{list.recipient_count !== 1 ? 's' : ''}
                    </button>
                  </div>
                  <div className="flex gap-1 ml-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(list)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(list)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit List' : 'Create New List'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="name">List Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Monthly Newsletter"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., General monthly updates for all members"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowForm(false)}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                variant="adminPrimary"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {editing ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Recipients Dialog */}
      <Dialog open={!!viewingRecipients} onOpenChange={() => setViewingRecipients(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Subscribers: {viewingRecipients?.name}</DialogTitle>
          </DialogHeader>
          <div className="pt-4">
            {loadingRecipients ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : recipients.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No subscribers yet</p>
            ) : (
              <div className="max-h-[400px] overflow-y-auto space-y-2">
                {recipients.map((recipient) => (
                  <div key={recipient.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                    <Users className="h-4 w-4 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{recipient.email}</p>
                      {recipient.name && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">{recipient.name}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
